import { ServiceBase } from "@app/shared";
import { ComparingZipAnalysisQueryParameters, ZipAnalysisQueryParameters, ZipAnalysisSearchOptions, ZipAnalysisSearchParameters, ZipClassAnalysisSearchOptionsResults, ZipClassAnalysisSearchParameters, ZipCodeAnalysisSearchOptionsResults, ZipDetailsApiResponse } from "./zip-projects/interfaces";
import { CloneProjectModel } from "./clone";
import Project from "./project";
import { querystringify } from "@app/shared/utility";
import { createDummyDropdownItem } from "./zip-projects/utility";
import { BulkRunSubmitResponseModel } from "./bulkRunSubmitResponseModel";
import { CreateZipProjectRequestModel } from "./zip-projects/interfaces/createZipProjectRequestModel";
import { ZipClassTrendChartItem, ZipCodeTrendChartItem } from "./zip-projects/interfaces/zipTrendChartItems";

/** Handles communication across the wire when retrieving and interacting with zip projects. */
export default class ZipProjectService extends ServiceBase {
  private readonly endpointPrefix = "zipprojects";

  public async createZipProject(createZipProjectRequestModel: CreateZipProjectRequestModel): Promise<void> {
    return await this.makePost(this.endpointPrefix, createZipProjectRequestModel);
  }

  /**
   * Clones a project as a ZipCodeAnalysisProject with provided settings
   * @param project The clone project model.
   * @param runNow Indicates whether to run the project immediately after creation or not
   */
  public async cloneProject(cloneProjectModel: CloneProjectModel, runNow: boolean): Promise<Project> {
    const query = querystringify({
      runNow,
    });

    return await this.makePost(`${this.endpointPrefix}/clone?${query}`, cloneProjectModel);
  }

  // also known as "submit"
  public async runZipProject(projectId: number): Promise<void> {
    return await this.makePost(`${this.endpointPrefix}/${projectId}/submit`);
  }

  public async bulkRunZipProjects(projectIds: number[]): Promise<BulkRunSubmitResponseModel> {
    if (projectIds.length > 0) {
      return await this.makePost(`${this.endpointPrefix}/submit`, projectIds);
    }
    return {
      submitResults: [],
    }
  }

  public async getFilteredRegionSetIds(projectId: number, runConfigurationId: number, zipAnalysisQuery: ZipAnalysisSearchParameters): Promise<number[]> {
    return await this.makePost(`${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-analysis-region-set-ids`, zipAnalysisQuery);
  }

  public async getZipCodeResults(projectId: number, runConfigurationId: number, zipAnalysisQuery: ZipAnalysisQueryParameters): Promise<ZipDetailsApiResponse[]> {
    const response = await this.makePost<ZipDetailsApiResponse[]>(`${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-analysis`, zipAnalysisQuery);
    return response || [{
      data: [],
      hasExpandableSpecialties: false,
      projectRunConfigurationId: runConfigurationId,
      resultsFor: "---",
      specialtyLevelResults: {},
      totalNumberOfRecords: 0,
    }];
  }

  public async getZipClassResults(projectId: number, runConfigurationId: number, zipAnalysisQuery: ZipAnalysisQueryParameters): Promise<ZipDetailsApiResponse[]> {
    const response = await this.makePost<ZipDetailsApiResponse[]>(`${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-class-aggregates`, zipAnalysisQuery);
    return response || [{
      data: [],
      hasExpandableSpecialties: false,
      projectRunConfigurationId: runConfigurationId,
      resultsFor: "---",
      specialtyLevelResults: {},
      totalNumberOfRecords: 0,
    }];
  }

  public async getZipCodeCompareModeResults(projectId: number, zipAnalysisQuery: ComparingZipAnalysisQueryParameters): Promise<ZipDetailsApiResponse[]> {
    const response = await this.makePost<ZipDetailsApiResponse[]>(`${this.endpointPrefix}/${projectId}/compared-zip-analysis`, zipAnalysisQuery);
    const newResponse: ZipDetailsApiResponse[] = [];
    response.forEach((r, index) => {
      if (!r) {
        r = {
          data: [],
          hasExpandableSpecialties: false,
          projectRunConfigurationId: index === 0 ? zipAnalysisQuery.projectRunConfigurationId1 : zipAnalysisQuery.projectRunConfigurationId2,
          resultsFor: "---",
          specialtyLevelResults: {},
          totalNumberOfRecords: 0,
        };
      }
      newResponse.push(r);
    })
    return newResponse;
  }

  public async getZipClassCompareModeResults(projectId: number, zipAnalysisQuery: ComparingZipAnalysisQueryParameters): Promise<ZipDetailsApiResponse[]> {
    const response = await this.makePost<ZipDetailsApiResponse[]>(`${this.endpointPrefix}/${projectId}/compared-zip-class-aggregates`, zipAnalysisQuery);
    const newResponse: ZipDetailsApiResponse[] = [];
    response.forEach((r, index) => {
      if (!r) {
        r = {
          data: [],
          hasExpandableSpecialties: false,
          projectRunConfigurationId: index === 0 ? zipAnalysisQuery.projectRunConfigurationId1 : zipAnalysisQuery.projectRunConfigurationId2,
          resultsFor: "---",
          specialtyLevelResults: {},
          totalNumberOfRecords: 0,
        };
      }
      newResponse.push(r);
    })
    return newResponse;
  }

  // getZipClassAggregateFilters in the API
  public async getZipClassFilterOptions(projectId: number, runConfigurationId: number, isSucceeded: boolean, compareToRunConfigurationId?: number): Promise<ZipAnalysisSearchOptions> {
    let apiResults: ZipClassAnalysisSearchOptionsResults[] = [];

    if (isSucceeded) {
      const body = !!compareToRunConfigurationId ? [compareToRunConfigurationId] : null;
      apiResults = await this.makePost<ZipClassAnalysisSearchOptionsResults[]>(`${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-class-aggregate-filters`, body);
    }

    return {
      city: [],
      county: [],
      metOverall: [
        {
          key: "all",
          name: "All",
        },
        ...[...(new Set(apiResults.flatMap((f) => f.filters.metOverallOptions || [])))].sort((a, b) => a > b ? -1 : 1).map(str => createDummyDropdownItem(str)),
      ],
      metPassPercent: [],
      specialtyGroupCode: [
        {
          display: "All Specialty Groups",
          key: "all",
          name: "All Specialty Groups",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.specialtyGroupCodes || [])))]).map(sg => {
          return {
            key: sg.specialtyGroupCode,
            name: `${sg.specialtyGroupCode} - ${sg.specialtyGroupDescription}`,
          };
        }),
      ],
      state: [],
      zipClass: [
        {
          display: "All Classifications",
          key: "all",
          name: "All Classifications",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.zipClasses || [])))].sort((a, b) => a > b ? 1 : -1) || []).map(str => createDummyDropdownItem(str)),
      ],
      zipCode: [],
    }
  }

  public async getZipCodeFilterOptions(projectId: number, runConfigurationId: number, isSucceeded: boolean, compareToRunConfigurationId?: number): Promise<ZipAnalysisSearchOptions> {
    let apiResults: ZipCodeAnalysisSearchOptionsResults[] = [];

    if (isSucceeded) {
      const body = !!compareToRunConfigurationId ? [compareToRunConfigurationId] : null;
      apiResults = await this.makePost<ZipCodeAnalysisSearchOptionsResults[]>(`${this.endpointPrefix}/${projectId}/run-configurations/${runConfigurationId}/zip-analysis-filters`, body);
    }

    return {
      city: [...(new Set(apiResults.flatMap((f) => f.filters.cities || [])))].sort((a, b) => a > b ? 1 : -1),
      county: [
        {
          display: "All Counties",
          key: "all",
          name: "All Counties",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.counties || [])))].sort((a, b) => a > b ? 1 : -1)).map(str => createDummyDropdownItem(str)),
      ],
      metOverall: [],
      metPassPercent: [
        {
          key: "all",
          name: "All",
        },
        ...[...(new Set(apiResults.flatMap((f) => f.filters.metPassPercent || [])))].sort((a, b) => a > b ? -1 : 1).map(str => createDummyDropdownItem(str)),
      ],
      specialtyGroupCode: [
        {
          display: "All Specialty Groups",
          key: "all",
          name: "All Specialty Groups",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.specialtyGroupCodes || [])))]).map(sg => {
          return {
            key: sg.specialtyGroupCode,
            name: `${sg.specialtyGroupCode} - ${sg.specialtyGroupDescription}`,
          };
        }),
      ],
      state: [
        {
          display: "All States",
          key: "all",
          name: "All States",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.stateNames || [])))].sort((a, b) => a > b ? 1 : -1)).map(str => createDummyDropdownItem(str)),
      ],
      zipClass: [
        {
          display: "All Classifications",
          key: "all",
          name: "All Classifications",
        },
        ...([...(new Set(apiResults.flatMap((f) => f.filters.zipClasses || [])))].sort((a, b) => a > b ? 1 : -1) || []).map(str => createDummyDropdownItem(str)),
      ],
      zipCode: [...(new Set(apiResults.flatMap((f) => f.filters.zipCodes || [])))].sort((a, b) => a > b ? 1 : -1),
    };
  }

  public async publishZipProject(projectId: number, comment = ""): Promise<void> {
    return await this.makePut(`${this.endpointPrefix}/${projectId}/publish?comment=${comment}`);
  }

  public async bulkPublishZipProjects(projectIds: number[], comment: string): Promise<number[]> {
    if (projectIds.length > 0) {
      return await this.makePost<number[]>(`${this.endpointPrefix}/publish`, { ProjectIds: projectIds, PublishComment: comment });
    }
    return [];
  }

  public async retryZipAnalysisByJobId(overrideJobQueueName: string, runConfigurationId: number, jobId: number): Promise<void> {
    const query = querystringify({ overrideJobQueueName });
    return await this.makePost(`${this.endpointPrefix}/run-configurations/${runConfigurationId}/jobs/${jobId}/retry?${query}`);
  }

  public async getZipClassTrendChartData(projectId: number, searchParameters: ZipClassAnalysisSearchParameters): Promise<ZipClassTrendChartItem[]> {
    return await this.makePost(`${this.endpointPrefix}/${projectId}/zip-class-trend-graph-data`, searchParameters);
  }

  public async getZipCodeTrendChartData(projectId: number, searchParameters: ZipAnalysisSearchParameters): Promise<ZipCodeTrendChartItem[]> {
    return await this.makePost(`${this.endpointPrefix}/${projectId}/zip-code-trend-graph-data`, searchParameters);
  }
}
